import { Injectable, Optional }           from '@angular/core';
import { Router }                          from '@angular/router';
import { AppQuery }                        from '@cs/performance-manager/shared';
import { FilterCompareBarQuery }           from '@cs/components/filter-and-compare-bar';
import { ReportingQuery }                  from '@cs/performance-manager/reporting';

/**
 * Composes the PM app's live "what is the user looking at" context from the state that already
 * exists — it introduces NO writable state of its own. Different pages hold their selection in
 * different places, so this reads them ALL and merges: the route (path + query params), the top-nav
 * scope (`AppQuery.currentAppParamsScope`, which the data-management filter navbar drives), and the
 * reporting filter-and-compare bar. The result is one compact block that rides with each chat request
 * (`ChatRequest.Context` on the server) so the assistant can answer "about this page" without asking.
 *
 * All queries are `providedIn: 'root'` singletons; `ReportingQuery` is `@Optional()` so the global
 * chat bubble still works on pages that never load the reporting feature.
 */
@Injectable({ providedIn: 'root' })
export class AgentContextService {

	constructor(
		private readonly app: AppQuery,
		private readonly filters: FilterCompareBarQuery,
		@Optional() private readonly reporting: ReportingQuery,
		private readonly router: Router
	) {}

	/** Keys that are routing plumbing, not user-facing filters — kept out of the "Filters" line. */
	private static readonly NOISE = new Set(['navfilterbar', 'businessline', 'scope', 'idteaser', 'module']);

	/**
	 * A compact snapshot of the current page/filters/report, built fresh from each store at send time.
	 * Empty string when nothing meaningful is set — the server then omits the context block. Kept short:
	 * it sits after the cached system prompt, so every token here is uncached per request.
	 */
	snapshot(): string {
		const lines: string[] = [];
		const state = this.app.getValue();
		const scope = state.currentAppParamsScope ?? {};

		// ── Where the user is: the top-nav section if set, else the route path (clearer than the
		// internal filter-bar name; the business line etc. still show under Filters). ──────────────
		const url       = this.router.url ?? '';
		const path      = url.split('?')[0].split('#')[0];
		const pageLabel = (typeof state.activeSection === 'string' && state.activeSection.trim())
			? state.activeSection
			: path;
		if (pageLabel && pageLabel !== '/') { lines.push('Page: ' + pageLabel + '.'); }

		// ── Two views of the filters, split on purpose ───────────────────────────────
		// "On screen" = the filter navbar EXACTLY AS SHOWN (friendly labels like "CDG - AF-G1XL"),
		// read from the DOM — for the assistant to understand what the user is looking at.
		// "Selection" = the underlying ids/keys (office id, scenario, KPI, grid, day range) — what the
		// assistant should map onto its query tools. Both are useful, so we keep both.
		const bar = this.readFilterBar();
		if (bar.length) { lines.push('On screen — ' + bar.join(', ') + '.'); }

		const tech = this.technicalSelection(scope, url);
		if (tech.length) { lines.push('Selection (ids/keys for querying) — ' + tech.join(', ') + '.'); }

		// ── The active report (deep-dive page), if the reporting feature is loaded ────
		const report = this.reporting?.getValue();
		if (report?.activeReport?.meta) {
			const m   = report.activeReport.meta;
			const tab = report.selection?.tabname;
			lines.push(`Active report: ${m.label || m.name}${tab ? ` (tab: ${tab})` : ''}.`);
		}

		return lines.join('\n').trim();
	}

	/**
	 * Reads the data-management filter navbar as the user sees it — one "Label: Value" per dropdown
	 * (Section, Topic, Location, View, Date). Values come from each toggle's text, except the Date
	 * whose value sits in an <input>. Returns [] when the bar isn't on the page (e.g. reporting).
	 */
	private readFilterBar(): string[] {
		if (typeof document === 'undefined') { return []; }
		const out: string[] = [];
		const seen = new Set<string>();
		document.querySelectorAll('.filter-container [class*="dropdown_"]').forEach(col => {
			const toggle = (col.querySelector('.advanced-dropdown-toggle') ?? col) as HTMLElement;
			const lines  = (toggle.innerText ?? '').split('\n').map(s => s.trim()).filter(Boolean);
			if (!lines.length) { return; }
			const label = lines[0];
			let   value = lines.slice(1).join(' ').trim();
			if (!value) {
				const input = col.querySelector('input') as HTMLInputElement | null;
				if (input?.value) { value = input.value.trim(); }
			}
			if (label && value && label !== value) {
				const bit = `${label}: ${value}`;
				if (!seen.has(bit)) { seen.add(bit); out.push(bit); }
			}
		});
		return out;
	}

	/**
	 * The underlying selection as ids/keys — from the filter bar's api params, the navbar scope, the
	 * URL query, and the result params' grid + day range. Deduped/humanised, with nested id objects
	 * unwrapped ({id} → the id, {minId,maxId} → a range). This is what the assistant maps onto its
	 * query tools (whereas "On screen" is the human-readable version).
	 */
	private technicalSelection(scope: { [k: string]: any }, url: string): string[] {
		const bits = new Map<string, string>();
		const add = (obj: { [k: string]: any } | undefined | null) => {
			if (!obj) { return; }
			for (const [key, value] of Object.entries(obj)) {
				if (bits.size >= 24) { break; }
				const label = this.humanize(key);
				if (bits.has(label)) { continue; }
				const val = this.techValue(value);
				if (val) { bits.set(label, val); }
			}
		};

		const fb = this.filters.getValue();
		add(fb.mainbarApiParams);              // LocationOffice, Date, KPI, Page, Scenario, View, businessLineKey
		add(scope);                            // navbar scope (deduped against the above)
		add(this.parseQuery(url));             // URL query params
		const rp = fb.mainbarResultParams as any;
		if (rp?.dataEntryGrid) { bits.set('Grid', String(rp.dataEntryGrid)); }
		const day = rp?.selection?.idDay;
		if (day && (day.minId || day.maxId)) { bits.set('Day range', `${day.minId ?? ''}–${day.maxId ?? ''}`); }
		if (fb.hasComparison)  { bits.set('Comparison', 'on'); }

		return [...bits]
			.filter(([k]) => !AgentContextService.NOISE.has(k.toLowerCase().replace(/\s+/g, '')))
			.map(([k, v]) => `${k}: ${v}`);
	}

	/** Scalar / simple-array / id-object → a display string; null when there's nothing usable. */
	private techValue(value: any): string | null {
		if (value === null || value === undefined || value === '') { return null; }
		const t = typeof value;
		if (t === 'string' || t === 'number' || t === 'boolean') { return String(value); }
		if (Array.isArray(value)) {
			const flat = value.filter(x => typeof x !== 'object');
			return flat.length ? flat.slice(0, 8).join(', ') : null;
		}
		if (t === 'object') {
			if (value.id !== undefined && value.id !== null) { return String(value.id); }
			if (value.minId !== undefined || value.maxId !== undefined) { return `${value.minId ?? ''}–${value.maxId ?? ''}`; }
		}
		return null;
	}

	/** Parse a URL's query string into a flat map (arrays like `a=[1,2]` become "1,2"). */
	private parseQuery(url: string): { [k: string]: string } {
		const q = url.split('?')[1];
		if (!q) { return {}; }
		const out: { [k: string]: string } = {};
		for (const pair of q.split('&')) {
			const eq  = pair.indexOf('=');
			const key = decodeURIComponent(eq >= 0 ? pair.slice(0, eq) : pair);
			const raw = eq >= 0 ? decodeURIComponent(pair.slice(eq + 1)) : '';
			if (key) { out[key] = raw.replace(/^\[|\]$/g, ''); }
		}
		return out;
	}

	/** camelCase / snake_case key → "Title Case" label. */
	private humanize(key: string): string {
		const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
		return spaced.charAt(0).toUpperCase() + spaced.slice(1);
	}
}
